import { useCallback, useContext, useEffect, useState } from "react";
import { ThemeContext } from "../../../../theme/ThemeContext";
import AuthContext from "../../../../auth/AuthContext";
import TicketDashboardService from "../../../../services/TicketDashboardService";
import AlertService from "../../../../services/AlertService";
import { Skeleton } from "primereact/skeleton"; // Import Skeleton

const primaryColor = "linear-gradient(90deg, #5A57FF 60%, #AAB1FF 100%)";
const lightColor = "#eee";

export default function TicketCategoriesChart() {
  const authContext = useContext(AuthContext);
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); 

const loadDashboard = useCallback(async (signal) => {
    setLoading(true);
    setError(null);
    try {
      const response = await TicketDashboardService.GetTicketCategoryWiseDashboard(
        authContext,
        signal 
      );

      if (signal?.aborted) return;

      if (response.isSuccess) {
        let loginCount = 0;
        let techCount = 0;
        let learnCount = 0;
        let quizCount = 0;
        if (response.data.filter(f => f.categoryId === 1).length > 0) {
          loginCount = response.data.filter(f => f.categoryId === 1)[0].count;
        }
        if (response.data.filter(f => f.categoryId === 2).length > 0) {
          techCount = response.data.filter(f => f.categoryId === 2)[0].count;
        }
        if (response.data.filter(f => f.categoryId === 3).length > 0) {
          learnCount = response.data.filter(f => f.categoryId === 3)[0].count;
        }
        if (response.data.filter(f => f.categoryId === 4).length > 0) {
          quizCount = response.data.filter(f => f.categoryId === 4)[0].count;
        }
        setData([
          { label: "Login", value: loginCount },
          { label: "Technical", value: techCount },
          { label: "Learning Material", value: learnCount },
          { label: "Quiz and Assessments", value: quizCount },
        ]);
      } else {
        setError(response.errorMessage);
        AlertService.error(response.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
      const errorMessage = "Failed to fetch ticket categories";
      setError(errorMessage);
      AlertService.error(errorMessage);
    } finally {
      if (!signal?.aborted) setLoading(false); 
    }
  }, [authContext]);

useEffect(() => {
    const controller = new AbortController(); 
    loadDashboard(controller.signal); 
    return () => controller.abort(); 
  }, [loadDashboard]);

  if (loading) {
    return (
      <div
        className="charts d-flex flex-column"
        style={{
          padding: 16,
          height: "337px",
          width: "100%",
          border: "1px solid #eee",
          borderRadius: "12px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 22,
          }}
        >
          <Skeleton width="120px" height="20px" /> 
        </div>

        {/* Bars Skeleton */}
        <div className="bars-wrapper">
          {[...Array(4)].map((_, index) => (
            <div key={index} className="bar-row" style={{ marginBottom: 10 }}>
              <Skeleton width="30%" height="20px" className="me-5" />
              <Skeleton width="60%" height="20px" /> 
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="charts d-flex flex-column"
        style={{
          padding: 16,
          height: "337px",
          width: "100%",
          border: "1px solid #eee",
          borderRadius: "12px",
          justifyContent: "center",
          alignItems: "center",
          color: "red",
        }}
      >
        {error}
      </div>
    );
  }

  return (
    <div
      className="charts d-flex flex-column"
      style={{
        padding: 16,
        height: "337px",
        width: "100%",
        border: "1px solid #eee",
        borderRadius: "12px",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 22,
        }}
      >
        <p style={{ margin: 0 }}>Ticket Categories</p>
     
      </div>

      {/* Bars */}
      <div className="bars-wrapper">
        {data.map((item) => (
          <div key={item.label} className="bar-row">
            <span
              style={{
                width: "40%",
                minWidth: "40%",
                fontSize: 16,
                color: isDark ? "#eaeaea" : "#444444",
                fontWeight: 500,
                marginRight: 10,
              }}
            >
              {item.label}
            </span>

            <div className="tooltip-container">
              <div className="bar-bg" style={{ background: lightColor }}>
                <div
                  className="bar-fill"
                  style={{
                    background: primaryColor,
                    width: `${item.value}%`,
                  }}
                />
              </div>
              <span className="tooltip-text">
                {item.label}: {item.value}%
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}